/**
 * Runtime shape detection shared by every client telemetry sink.
 *
 * These properties are the difference between "conversion dropped" and
 * "conversion dropped *on Android native*". They are registered once as
 * PostHog super properties and stamped onto client logs and exceptions, so
 * every dashboard can break down by surface without extra instrumentation.
 */

type CapacitorGlobal = {
  isNativePlatform?: () => boolean;
  getPlatform?: () => string;
};

function readCapacitor(): CapacitorGlobal | undefined {
  if (typeof window === "undefined") return undefined;
  return (window as Window & { Capacitor?: CapacitorGlobal }).Capacitor;
}

export function isNativeCapacitorRuntime(): boolean {
  return Boolean(readCapacitor()?.isNativePlatform?.());
}

export type AppSurface = "ios_native" | "android_native" | "pwa" | "web";

export function readNativePlatform(): string | undefined {
  const platform = readCapacitor()?.getPlatform?.();
  return platform && platform !== "web" ? platform : undefined;
}

export function isStandalonePwa(): boolean {
  if (typeof window === "undefined") return false;

  const iosStandalone = (
    window.navigator as Navigator & { standalone?: boolean }
  ).standalone;

  return Boolean(
    iosStandalone ||
      window.matchMedia?.("(display-mode: standalone)")?.matches ||
      window.matchMedia?.("(display-mode: fullscreen)")?.matches,
  );
}

export function resolveAppSurface(): AppSurface {
  const nativePlatform = readNativePlatform();
  if (nativePlatform === "ios") return "ios_native";
  if (nativePlatform === "android") return "android_native";
  if (isStandalonePwa()) return "pwa";
  return "web";
}

/**
 * Properties describing *where* the client is running. Cheap to compute and
 * stable for the lifetime of the document.
 */
export function buildRuntimeContext() {
  const surface = resolveAppSurface();

  return {
    app_surface: surface,
    is_native: surface === "ios_native" || surface === "android_native",
    native_platform: readNativePlatform() ?? "web",
    is_standalone_pwa: isStandalonePwa(),
  };
}

/**
 * Volatile runtime facts worth attaching to an error report — an error that
 * only happens offline or backgrounded is a different bug from one that does
 * not.
 */
export function buildErrorRuntimeContext() {
  if (typeof window === "undefined") return {};

  return {
    pathname: window.location.pathname,
    online: navigator.onLine,
    visibility_state: document.visibilityState,
    viewport_width: window.innerWidth,
    viewport_height: window.innerHeight,
  };
}
